import { Fragment, useMemo, useState } from 'react';
import {
  EVENT_FIELDS,
  TYPE_FIELDS,
  readEconomyRows,
  type EconomyRow,
  type EconomyTag,
  type EditorDoc,
} from '../lib/model';
import { CATEGORY_TAGS, CONTAINER_TAGS, USAGE_TAGS, VALUE_TAGS } from '../lib/samples';
import { useDispatch } from '../state/store';

const PAGE_SIZE = 40;

/**
 * Table editor for `events.xml` (how much of each event spawns) and
 * `types.xml` (loot economy, including the usage / tier tags that decide
 * which parts of the map an item group can spawn in).
 */
export function EconomyPanel({ doc }: { doc: EditorDoc }) {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  const rows = useMemo(() => readEconomyRows(doc), [doc]);
  const fields = doc.kind === 'events' ? EVENT_FIELDS : TYPE_FIELDS;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (row) =>
        row.name.toLowerCase().includes(needle) ||
        row.tags.usage.some((tag) => tag.toLowerCase().includes(needle)) ||
        row.tags.value.some((tag) => tag.toLowerCase().includes(needle)) ||
        row.tags.category.some((tag) => tag.toLowerCase().includes(needle)),
    );
  }, [rows, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="economy">
      <div className="economy-head">
        <h2>{doc.fileName}</h2>
        <input
          type="search"
          placeholder={doc.kind === 'events' ? 'Filter events…' : 'Filter items, usage or tier…'}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
        />
        <span className="muted">
          {filtered.length} of {rows.length}
        </span>
        {pageCount > 1 && (
          <span className="pager">
            <button type="button" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
              ‹
            </button>
            {safePage + 1} / {pageCount}
            <button type="button" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>
              ›
            </button>
          </span>
        )}
      </div>

      <div className="table-scroll">
        <table className="economy-table">
          <thead>
            <tr>
              <th className="name-col">name</th>
              {fields.map((field) => (
                <th key={field}>{field}</th>
              ))}
              {doc.kind === 'types' && <th className="tags-col">usage / tier</th>}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <Fragment key={row.id}>
                <tr>
                  <td className="name-col">
                    {doc.kind === 'types' ? (
                      <button type="button" className="link" onClick={() => setExpanded(expanded === row.id ? null : row.id)}>
                        {row.name}
                      </button>
                    ) : (
                      row.name
                    )}
                  </td>
                  {fields.map((field) => (
                    <td key={field}>
                      <input
                        className="cell"
                        value={row.fields[field] ?? ''}
                        placeholder="–"
                        onChange={(e) =>
                          dispatch({
                            type: 'economySetField',
                            docId: doc.id,
                            rowId: row.id,
                            field,
                            value: e.target.value,
                          })
                        }
                      />
                    </td>
                  ))}
                  {doc.kind === 'types' && (
                    <td className="tags-col">
                      <span className="tags">
                        {[...row.tags.usage, ...row.tags.value].join(', ') || <span className="muted">none</span>}
                      </span>
                    </td>
                  )}
                </tr>
                {expanded === row.id && doc.kind === 'types' && (
                  <tr className="tag-editor-row">
                    <td colSpan={fields.length + 2}>
                      <TagEditor docId={doc.id} rowId={row.id} tags={row.tags} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="hint">
        {doc.kind === 'events'
          ? 'nominal / min / max control how many of each event exist at once. Their positions live in cfgeventspawns.xml - edit those on the Map tab.'
          : 'usage tags pick the building categories an item spawns in, tier tags pick how far inland. Together they decide where an item group appears on the map.'}
      </p>
    </div>
  );
}

/** Chip toggles for an item's usage / tier / category / container-tag elements in types.xml. */
export function TagEditor({
  docId,
  rowId,
  tags,
}: {
  docId: string;
  rowId: string;
  tags: EconomyRow['tags'];
}) {
  const dispatch = useDispatch();

  const renderGroup = (label: string, tag: EconomyTag, options: string[], active: string[]) => (
    <div className="tag-group">
      <span className="tag-label">{label}</span>
      <div className="tag-chips">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={`chip ${active.includes(option) ? 'on' : ''}`}
            onClick={() => dispatch({ type: 'economyToggleTag', docId, rowId, tag, name: option })}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="tag-editor">
      {renderGroup('usage', 'usage', USAGE_TAGS, tags.usage)}
      {renderGroup('tier', 'value', VALUE_TAGS, tags.value)}
      {renderGroup('category', 'category', CATEGORY_TAGS, tags.category)}
      {renderGroup('loot spot', 'tag', CONTAINER_TAGS, tags.tag)}
    </div>
  );
}
